import Vuex from 'vuex';
import Vue from 'vue';
import {User, Plan} from '@/model/User';
import {Auth} from '@/plugins/Auth';
import {ExternalLinks} from "@/config/ExternalLinks";
import {Firestore} from "@/plugins/Firestore";
import {I18n} from "@/plugins/I18n";
import {captureException} from "@/plugins/Sentry";
import {
    Article,
    ArticleContentPartStorePath, ArticleContentStorePath,
    ArticleLoadings,
    ArticleMetaPartStorePath,
    ArticleQuestionPartStorePath, ArticleQuestionStorePath,
    ArticleWordCounts, getDefaultForContent, getDefaultForQuestion,
    KeywordForAi,
    PolishState
} from "@/model/Article";
import {stripMarkers, stripTagsAndEntities} from "@/utils/string";
import {getUid} from "@/utils/uid";
import {getArticleSnapshotHash} from "@/utils/ArticleComputed";
import {ScrapperResponse, Image, ScrapedFaq} from "@/model/Scrapper";
import {EatAttribution, hasEatAuthor, normalizeEatAttribution} from "@/service/extractEatAttribution";
import {faqContentPartsToRemove} from "@/service/articleFaqFromUrl";
import toasts from '@/plugins/modules/Toasts';
import { Toast } from '@/model/Toast';
import { FreeAnalysesConfig, remainingFreeAnalyses } from '@/config/FreeAnalyses';
import {resolveArticleMainKeyword} from '@/service/articleMainKeyword';
import {canAssignSeoScore} from '@/service/seoScoreCommit';
import {importScrapedBlocks, sanitizeImportedImageAlt} from '@/service/articleSanitize';
import type {HeadingRole} from '@/service/articleSanitize';
import {cleanScrapedQuestions} from '@/service/scrapJunkFilter';

Vue.use(Vuex);

class State {
    newContent = false;
    user: User | null | undefined = undefined;
    /** Undefined until getCurrentPlan settles. Null means a loaded account with no paid plan. */
    plan: Plan | null | undefined = undefined;
    planLoaded = false;
    /** False until observeUserDoc delivers articlesCreatedThisPeriod. Login's 0 is not that counter. */
    usageCounterLoaded = false;
    maxFreeAnalyses = FreeAnalysesConfig.defaultCount;
    analyzeArticlesThisMonth = 0;
    drawer = true;
    article: Article = new Article();
    articleAiLoading: ArticleLoadings = new ArticleLoadings();
    articleAiPolishing = false;
    articleAiEnhancing = false;
    articleAiFailed = false;
    articleSaving = false;
    articleSeoScoreChecked = false;
    articleWordCount: ArticleWordCounts = new ArticleWordCounts();
    articleLastSavedSnapshotHash: string | null = null;
    articleAnalyzeScrapeLoading = false;
    wordOccurrences: Record<string, number> = {};
    keywordSelectionShown = false;
    keywordsForAi: KeywordForAi[] = [];
    sharePreview = false;
    sharePreviewToken: string | null = null;
}

const Store = new Vuex.Store({
    state: (new State()),
    modules: {
        toasts
    },
    getters: {
        newContent: (state: State): boolean => state.newContent,
        user: (state: State): User | null | undefined => state.user,
        authenticated: (state: State): boolean => (state.user !== null && state.user !== undefined),
        plan: (state: State): Plan | null | undefined => state.plan,
        planLoaded: (state: State): boolean => state.planLoaded,
        usageCounterLoaded: (state: State): boolean => state.usageCounterLoaded,
        hasPlan: (state: State): boolean => (state.plan !== null && state.plan !== undefined),
        maxFreeAnalyses: (state: State): number => state.maxFreeAnalyses,
        analyzeArticlesThisMonth: (state: State): number => state.analyzeArticlesThisMonth,
        drawer: (state: State): boolean => state.drawer,
        // Article
        article: (state: State): Article => state.article,
        articleWordCount: (state: State): ArticleWordCounts => state.articleWordCount,
        articleAiLoading: (state: State): ArticleLoadings => state.articleAiLoading,
        articleLastSavedSnapshotHash: (state: State): string | null => state.articleLastSavedSnapshotHash,
        articleAnalyzeScrapeLoading: (state: State): boolean => state.articleAnalyzeScrapeLoading,
        articleAiPolishing: (state: State): boolean => state.articleAiPolishing,
        articleAiEnhancing: (state: State): boolean => state.articleAiEnhancing,
        articleAiFailed: (state: State): boolean => state.articleAiFailed,
        articleSaving: (state: State): boolean => state.articleSaving,
        // Unlock as soon as generate saves. Polish-as-step-2 no longer holds the editor.
        articleContentReady: (state: State): boolean => state.articleAiLoading.loadingCompleted && !state.articleSaving && state.articleSeoScoreChecked,
        articleSeoScoreChecked: (state: State): boolean => state.articleSeoScoreChecked,
        articleKeywordsForAi: (state: State): KeywordForAi[] => state.keywordsForAi,
        // Article computed
        wordOccurrences: (state: State): Record<string, number> => state.wordOccurrences,
        // Keyword selection
        keywordSelectionShown: (state: State): boolean => state.keywordSelectionShown,
        sharePreview: (state: State): boolean => state.sharePreview,
        sharePreviewToken: (state: State): string | null => state.sharePreviewToken,
    },
    mutations: {
        newContent: (state: State, value: boolean): void => {
            state.newContent = value;
        },
        setUser: (state: State, value: User): void => {
            state.user = value;
        },
        setPlan: (state: State, value: Plan | null): void => {
            state.plan = value;
        },
        setPlanLoaded: (state: State, value: boolean): void => {
            state.planLoaded = value;
        },
        setUsageCounterLoaded: (state: State, value: boolean): void => {
            state.usageCounterLoaded = value;
        },
        setMaxFreeAnalyses: (state: State, value: number): void => {
            state.maxFreeAnalyses = value;
        },
        setAnalyzeArticlesThisMonth: (state: State, value: number): void => {
            state.analyzeArticlesThisMonth = value;
        },
        setUserTheme: (state: State, value: boolean): void => {
            if (state.user) {
                state.user.theme = value;
            }
        },
        setUserArticlesCreatedThisPeriod: (state: State, value: number): void => {
            if (state.user) {
                state.user.articlesCreatedThisPeriod = value;
            }
        },
        setUserFreeAnalysesLeft: (state: State, value: number): void => {
            if (state.user) {
                state.user.freeAnalysesLeft = value;
            }
        },
        setUserExtraInspirations: (state: State, value: number): void => {
            if (state.user) {
                state.user.extraInspirations = value;
            }
        },
        logout: (state): void => {
            state.user = null;
            state.plan = null;
            state.planLoaded = false;
            state.usageCounterLoaded = false;
            state.analyzeArticlesThisMonth = 0;
            state.sharePreview = false;
            state.sharePreviewToken = null;
        },
        setDrawer: (state: State, value: boolean): void => {
            state.drawer = value;
        },
        // Article // TODO in Pinia move to separate store
        insertArticleContentSubPart: (state: State, payload: ArticleContentStorePath): void => {
            if (!payload.keepContent) {
                state.article[payload.contentPart].splice(payload.index, 0, getDefaultForContent());
                state.articleWordCount[payload.contentPart].splice(payload.index, 0, getDefaultForContent<number>(0));
            }
            state.articleAiLoading[payload.contentPart].splice(payload.index, 0, getDefaultForContent<boolean>(!!payload.aiLoading));
        },
        insertArticleQuestion: (state: State, payload: ArticleQuestionStorePath): void => {
            state.article.questions.splice(payload.index, 0, getDefaultForQuestion());
            state.articleWordCount.questions.splice(payload.index, 0, getDefaultForQuestion<number>(0));
            state.articleAiLoading.questions.splice(payload.index, 0, getDefaultForQuestion<boolean>(!!payload.aiLoading));
        },
        removeArticleContentSubPart: (state: State, payload: ArticleContentStorePath): void => {
            state.article[payload.contentPart].splice(payload.index, 1);
            state.articleWordCount[payload.contentPart].splice(payload.index, 1);
            state.articleAiLoading[payload.contentPart].splice(payload.index, 1);
        },
        // Atomically replace one heading-level array and its parallel wordCount/aiLoading arrays.
        // Used by the drag-and-drop handler so all three arrays are always updated in a single
        // commit — prevents the intermediate inconsistent state that caused render errors.
        setArticleContentArray: (state: State, payload: { contentPart: string, newArray: any[] }): void => {
            const currentArray  = (state.article as any)[payload.contentPart] as any[];
            const currentWC     = (state.articleWordCount as any)[payload.contentPart] as any[];
            const currentAL     = (state.articleAiLoading as any)[payload.contentPart] as any[];

            // Build uid → parallel-row maps for the CURRENT state of this part.
            // Blocks dragged in from another part won't be found here; for those we
            // recompute word-counts from their content (result is identical to stored value).
            const wcByUid: Record<number, any> = {};
            const alByUid: Record<number, any> = {};
            currentArray.forEach((block: any, idx: number) => {
                wcByUid[block.uid] = currentWC[idx];
                alByUid[block.uid] = currentAL[idx];
            });

            const newWordCount = payload.newArray.map((block: any) => {
                if (wcByUid[block.uid]) return wcByUid[block.uid];
                const hWords = stripTagsAndEntities(block.h || '').split(' ').filter((w: string) => w.length > 0).length;
                const pWords = stripTagsAndEntities(block.p || '').split(' ').filter((w: string) => w.length > 0).length;
                return { h: hWords, p: pWords, uid: block.uid };
            });
            const newAiLoading = payload.newArray.map((block: any) =>
                alByUid[block.uid] || { h: false, p: false, uid: block.uid }
            );

            (state.article as any)[payload.contentPart]      = payload.newArray;
            (state.articleWordCount as any)[payload.contentPart]  = newWordCount;
            (state.articleAiLoading as any)[payload.contentPart]  = newAiLoading;
        },
        // Rebuild h2-h6 arrays from a flat ordered list produced by the TOC drag.
        // Each entry carries the block uid and its new heading level (2-6).
        // h1 is never touched.
        setArticleFromFlatBlocks: (state: State, flatBlocks: Array<{ uid: number, level: number }>): void => {
            // Snapshot all blocks + parallel arrays across h1-h6 by uid
            const blocksByUid: Record<number, any> = {};
            const wcByUid: Record<number, any> = {};
            const alByUid: Record<number, any> = {};
            for (let i = 1; i <= 6; i++) {
                const part = 'h' + i;
                ((state.article as any)[part] as any[]).forEach((block: any, idx: number) => {
                    blocksByUid[block.uid] = block;
                    wcByUid[block.uid] = (state.articleWordCount as any)[part][idx];
                    alByUid[block.uid] = (state.articleAiLoading as any)[part][idx];
                });
            }
            // Build new arrays per heading level
            const newA: Record<string, any[]> = { h2: [], h3: [], h4: [], h5: [], h6: [] };
            const newW: Record<string, any[]> = { h2: [], h3: [], h4: [], h5: [], h6: [] };
            const newL: Record<string, any[]> = { h2: [], h3: [], h4: [], h5: [], h6: [] };
            for (const { uid, level } of flatBlocks) {
                const part = 'h' + level;
                if (blocksByUid[uid] && newA[part] !== undefined) {
                    newA[part].push(blocksByUid[uid]);
                    newW[part].push(wcByUid[uid] || { h: 0, p: 0, uid });
                    newL[part].push(alByUid[uid] || { h: false, p: false, uid });
                }
            }
            for (let i = 2; i <= 6; i++) {
                const part = 'h' + i;
                (state.article as any)[part] = newA[part];
                (state.articleWordCount as any)[part] = newW[part];
                (state.articleAiLoading as any)[part] = newL[part];
            }
        },
        removeArticleQuestion: (state: State, index: number): void => {
            state.article.questions.splice(index, 1);
            state.articleWordCount.questions.splice(index, 1);
            state.articleAiLoading.questions.splice(index, 1);
        },
        replaceArticleQuestions: (state: State, faqs: ScrapedFaq[]): void => {
            Vue.set(state.article, 'questions', faqs.map((item) => {
                const question = getDefaultForQuestion();
                question.question = stripMarkers(item.question || '');
                question.answer = stripMarkers(item.answer || '');
                return question;
            }));
            Vue.set(state.articleWordCount, 'questions', faqs.map((item) => {
                const counts = getDefaultForQuestion<number>(0);
                const questionText = stripTagsAndEntities(item.question || '');
                const answerText = stripTagsAndEntities(item.answer || '');
                counts.question = questionText ? questionText.split(' ').length : 0;
                counts.answer = answerText ? answerText.split(' ').length : 0;
                return counts;
            }));
            Vue.set(state.articleAiLoading, 'questions', faqs.map(() => getDefaultForQuestion<boolean>(false)));
        },
        setArticleMeta: (state: State, payload: ArticleMetaPartStorePath): void => {
            const noHtmlContent = stripTagsAndEntities(payload.value);
            const wordCount = noHtmlContent === '' ? 0 : noHtmlContent.split(' ').length;
            state.article[payload.metaPart] = payload.value;
            state.articleWordCount[payload.metaPart] = wordCount;
        },
        setArticleContent: (state: State, payload: ArticleContentPartStorePath): void => {
            const noHtmlContent = stripTagsAndEntities(payload.value);
            const wordCount = noHtmlContent === '' ? 0 : noHtmlContent.split(' ').length;
            if (!state.article[payload.contentPart][payload.index]) {
                Vue.set(state.article[payload.contentPart], payload.index, getDefaultForContent());
            }
            if (!state.articleWordCount[payload.contentPart][payload.index]) {
                Vue.set(state.articleWordCount[payload.contentPart], payload.index, getDefaultForContent<number>(0));
            }
            state.article[payload.contentPart][payload.index][payload.contentKey] = stripMarkers(payload.value);
            if (!state.article[payload.contentPart][payload.index].uid) {
                state.article[payload.contentPart][payload.index].uid = getUid();
            }
            state.articleWordCount[payload.contentPart][payload.index][payload.contentKey] = wordCount;
        },
        setArticleQuestion: (state: State, payload: ArticleQuestionPartStorePath): void => {
            const noHtmlContent = stripTagsAndEntities(payload.value);
            const wordCount = noHtmlContent === '' ? 0 : noHtmlContent.split(' ').length;
            if (!state.article.questions[payload.index]) {
                Vue.set(state.article.questions, payload.index, getDefaultForQuestion());
            }
            if (!state.articleWordCount.questions[payload.index]) {
                Vue.set(state.articleWordCount.questions, payload.index, getDefaultForQuestion<number>(0));
            }
            state.article.questions[payload.index][payload.questionKey] = stripMarkers(payload.value);
            if (!state.article.questions[payload.index].uid) {
                state.article.questions[payload.index].uid = getUid();
            }
            state.articleWordCount.questions[payload.index][payload.questionKey] = wordCount;
        },
        setArticleImages: (state: State, payload: { images: Image[] }): void => {
            state.article.images = payload.images;
        },
        setArticleUrl: (state: State, url: string): void => {
            state.article.url = url;
        },
        setArticleSchemaJsonLd: (state: State, schemaJsonLd: string): void => {
            state.article.schemaJsonLd = schemaJsonLd;
        },
        addArticleImage: (state: State, payload: Image): void => {
            state.article.images.push(payload);
        },
        removeArticleImage: (state: State, index: number): void => {
            state.article.images.splice(index, 1);
        },
        setArticleMetaAiLoading: (state: State, payload: ArticleMetaPartStorePath<boolean>): void => {
            state.articleAiLoading[payload.metaPart] = payload.value;
        },
        setArticleContentAiLoading: (state: State, payload: ArticleContentPartStorePath<boolean>): void => {
            if (!state.articleAiLoading[payload.contentPart][payload.index]) {
                Vue.set(state.articleAiLoading[payload.contentPart], payload.index, getDefaultForContent<boolean>(false));
            }
            state.articleAiLoading[payload.contentPart][payload.index][payload.contentKey] = payload.value;
        },
        setArticleQuestionAiLoading: (state: State, payload: ArticleQuestionPartStorePath<boolean>): void => {
            if (!state.articleAiLoading.questions[payload.index]) {
                Vue.set(state.articleAiLoading.questions, payload.index, getDefaultForQuestion<boolean>(false));
            }
            state.articleAiLoading.questions[payload.index][payload.questionKey] = payload.value;
        },
        setWordOccurrences: (state: State, payload: [word: string, occurrences: number]): void => {
            Vue.set(state.wordOccurrences, payload[0], payload[1]);
        },
        setArticleSeoScore: (state: State, value: number): void => {
            if (!canAssignSeoScore(state.article.seoScore, value)) {
                return;
            }
            state.article.seoScore = value;
        },
        setArticleSeoScoreAiLoadingCompleted: (state: State, value: boolean): void => {
            state.articleAiLoading.loadingCompleted = value;
        },
        setArticleLastSavedSnapshotHash: (state: State, value: string | null): void => {
            state.articleLastSavedSnapshotHash = value;
        },
        setArticleAnalyzeScrapeLoading: (state: State, value: boolean): void => {
            state.articleAnalyzeScrapeLoading = value;
        },
        setArticleForcedEmpty: (state: State, value: boolean): void => {
            state.article.forcedEmpty = value;
        },
        setArticleAiPolishing: (state: State, value: boolean): void => {
            state.articleAiPolishing = value;
        },
        setArticlePolish: (state: State, value: PolishState): void => {
            state.article.polish = value;
        },
        setArticleSaving: (state: State, value: boolean): void => {
            state.articleSaving = value;
        },
        setArticleSeoScoreChecked: (state: State, value: boolean): void => {
            state.articleSeoScoreChecked = value;
        },
        setArticleAiEnhancing: (state: State, value: boolean): void => {
            state.articleAiEnhancing = value;
        },
        setArticleAiFailed: (state: State, value: boolean): void => {
            state.articleAiFailed = value;
        },
        trimArticleAiLoading: (state: State): void => {
            for (let level = 1; level <= 6; level++) {
                const part = 'h' + level;
                const articleLen = ((state.article as any)[part] || []).length;
                const loaders = (state.articleAiLoading as any)[part] as any[];
                if (Array.isArray(loaders) && loaders.length > articleLen) {
                    loaders.splice(articleLen);
                }
            }
            const questionLen = (state.article.questions || []).length;
            if (state.articleAiLoading.questions.length > questionLen) {
                state.articleAiLoading.questions.splice(questionLen);
            }
        },
        setKeywordSelectionShown: (state: State, value: boolean): void => {
            state.keywordSelectionShown = value;
        },
        setKeywordsForAi: (state: State, value: KeywordForAi[]): void => {
            state.keywordsForAi = value;
        },
        setSharePreview: (state: State, payload: {active: boolean; token?: string | null}): void => {
            state.sharePreview = !!payload.active;
            state.sharePreviewToken = payload.token || null;
        },
        clearSharePreview: (state: State): void => {
            state.sharePreview = false;
            state.sharePreviewToken = null;
        },
        setArticleEatAttribution: (state: State, value: Partial<EatAttribution>): void => {
            Vue.set(state.article, 'eatAttribution', normalizeEatAttribution({
                ...state.article.eatAttribution,
                ...value,
            }));
        }
    },
    actions: {
        login: async ({commit, dispatch}, user: User) => {
            commit('setPlanLoaded', false);
            commit('setUsageCounterLoaded', false);
            commit('setUser', user);
            commit('setUserArticlesCreatedThisPeriod', user.articlesCreatedThisPeriod ?? 0);
            commit('setUserExtraInspirations', user.extraInspirations ?? 0);
            try {
                await Firestore.ensureUserDoc(user.uid, user.email);
            } catch (e) {
                console.error('[Store] ensureUserDoc failed:', e);
            }
            try {
                commit('setPlan', await Firestore.getCurrentPlan() || null);
            } catch (e) {
                console.error('[Store] getCurrentPlan failed:', e);
                commit('setPlan', null);
            } finally {
                commit('setPlanLoaded', true);
            }
            try {
                Firestore.observeUserDoc((value) => {
                    commit('setUserTheme', value.theme);
                    commit('setUserArticlesCreatedThisPeriod', value.articlesCreatedThisPeriod);
                    commit('setUsageCounterLoaded', true);
                    commit('setUserExtraInspirations', value.extraInspirations);
                    dispatch('reconcileFreeAnalyses', value.freeAnalysesLeft);
                });
            } catch (e) {
                console.error('[Store] observeUserDoc failed:', e);
            }
        },
        // Display only: ArticleOnCreateObserver and the monthly reset own
        // freeAnalysesLeft, and firestore.rules refuse a browser write (#30909).
        reconcileFreeAnalyses: async ({commit, getters}, storedValue: number | undefined) => {
            if (!getters.planLoaded) {
                return;
            }
            // v1.14.2 left -1 for some users; the server charges that as 0 (#30943).
            const stored = typeof storedValue === 'number' && storedValue < 0 ? 0 : storedValue;
            try {
                const used = await Firestore.countAnalyzeArticlesThisMonth();
                if (!getters.user) {
                    return;
                }
                commit('setAnalyzeArticlesThisMonth', used);
                if (getters.hasPlan) {
                    const left = typeof stored === 'number' && stored >= 0
                        ? stored
                        : getters.maxFreeAnalyses;
                    commit('setUserFreeAnalysesLeft', left);
                    return;
                }
                commit('setUserFreeAnalysesLeft', remainingFreeAnalyses(stored, used, getters.maxFreeAnalyses));
            } catch (e) {
                console.error('[Store] reconcileFreeAnalyses failed:', e);
                if (!getters.user) {
                    return;
                }
                const remaining = remainingFreeAnalyses(
                    stored,
                    getters.analyzeArticlesThisMonth,
                    getters.maxFreeAnalyses
                );
                commit('setUserFreeAnalysesLeft', remaining);
            }
        },
        logout: ({commit, dispatch}): void => {
            Firestore.stopObservingUser();
            Auth.logout()
                .then(() => {
                    window.location.assign(ExternalLinks.logoutRedirect);
                    commit('logout');
                })
                .catch(error => {
                    dispatch('toasts/add', {
                        type: 'error',
                        title: 'unknownError',
                    } as Toast)
                    captureException(error);
                });
        },
        noUser: ({commit}): void => {
            commit('setUser', null);
        },
        handleApiError: ({dispatch}, error: string | unknown = "unexpectedError"): void => {
            let userError = error;
            if (typeof error !== "string" || !I18n.te(error)) {
                userError = 'unexpectedError';
                window.console.error(error);
            }
            dispatch('toasts/add', {
                type: 'error',
                title: userError,
            } as Toast)
            captureException(error);
        },
        // Article
        setArticle: ({state, commit}, article: Article): void => {
            state.article = article;
            state.articleAiLoading = new ArticleLoadings;
            state.articleWordCount = new ArticleWordCounts;
            state.articleAiPolishing = false;
            state.articleAiEnhancing = false;
            state.articleAiFailed = false;
            state.articleSaving = false;
            state.articleSeoScoreChecked = false;
            commit('setKeywordsForAi', Array.isArray(article.keywordsForAi) ? article.keywordsForAi.slice() : []);
            commit('setKeywordSelectionShown', false);

            for (const meta of ['metaTitle', 'metaDescription']) {
                commit('setArticleMeta', <ArticleMetaPartStorePath>{
                    metaPart: meta,
                    value: (article as any)[meta]
                });
            }

            for (const part of article.getAllParts()) {
                for (const [index, value] of part.value.entries()) {
                    for (const key of ['h', 'p']) {
                        // To trigger all side effects like wordCounts
                        commit('setArticleContent', <ArticleContentPartStorePath>{
                            contentPart: 'h' + part.number,
                            index: index,
                            contentKey: key,
                            value: (value as any)[key]
                        });
                    }
                }
            }

            for (const [index, question] of article.questions.entries()) {
                for (const key of ['question', 'answer']) {
                    commit('setArticleQuestion', <ArticleQuestionPartStorePath>{
                        index: index,
                        questionKey: key,
                        value: (question as any)[key]
                    });
                }
            }
        },
        setArticleLastSavedSnapshotHash: ({commit, getters}): void => {
            getArticleSnapshotHash(getters.article).then(hash => {
                commit('setArticleLastSavedSnapshotHash', hash);
            });
        },
        setArticleScrapedContent: ({ commit, dispatch, getters }, response: ScrapperResponse): Promise<void> => {
            const mainKeyword = resolveArticleMainKeyword(getters.article || {});
            for (const meta of ['metaTitle', 'metaDescription']) {
                commit('setArticleMeta', <ArticleMetaPartStorePath>{
                    metaPart: meta,
                    value: (response as any)[meta] || ''
                });
            }
            for (let i = 1; i <= 6; ++i) {
                const headings = (response as any)['h' + i + 'h'] || [];
                const paragraphs = (response as any)['h' + i + 'p'] || [];
                const count = Math.max(headings.length, paragraphs.length);
                if (!count) {
                    continue;
                }
                const role: HeadingRole = i === 1 ? 'h1' : i === 2 ? 'h2' : 'h3';
                const blocks = importScrapedBlocks(headings, paragraphs, mainKeyword, role);
                blocks.forEach((block, outIndex) => {
                    commit('setArticleContent', <ArticleContentPartStorePath>{
                        contentPart: 'h' + i,
                        index: outIndex,
                        contentKey: 'h',
                        value: block.h,
                    });
                    commit('setArticleContent', <ArticleContentPartStorePath>{
                        contentPart: 'h' + i,
                        index: outIndex,
                        contentKey: 'p',
                        value: block.p,
                    });
                });
            }
            if (response.images.length) {
                const images = [];
                const count = Math.min(response.images.length, 40);
                for (let i = 0; i < count; i++) {
                    const image = response.images[i];
                    if (!image) {
                        continue;
                    }
                    images.push({
                        ...image,
                        alt: sanitizeImportedImageAlt(image.alt),
                    });
                }
                for (let i = count; i < response.images.length; i++) {
                    if (response.images[i]) {
                        images.push(response.images[i]);
                    }
                }
                commit('setArticleImages', {images});
            }
            const faqs = cleanScrapedQuestions(response.questions || []);
            if (faqs.length) {
                dispatch('applyScrapedFaqs', faqs);
            }
            if (response.eatAttribution && hasEatAuthor(response.eatAttribution)) {
                commit('setArticleEatAttribution', response.eatAttribution);
            }
            return Promise.resolve();
        },
        applyScrapedFaqs: ({state, commit}, faqs: ScrapedFaq[]): void => {
            if (!faqs || !faqs.length) {
                return;
            }
            for (const removal of faqContentPartsToRemove(state.article, faqs)) {
                commit('removeArticleContentSubPart', removal);
            }
            commit('replaceArticleQuestions', faqs);
        },
        setArticleForcedEmpty: ({commit}): Promise<void> => {
            commit('setArticleForcedEmpty', true);
            return Promise.resolve();
        }
    }
});

export {Store, State};
